/** Browser-side guard before sync uploads or catalog downloads (cheap reachability hint, not a full QoS test). */
const SYNC_PREFLIGHT_TIMEOUT_MS = 10_000;

function isLikelyTimeoutAbort(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  return e.name === 'TimeoutError' || e.name === 'AbortError';
}

function timeoutMessage(): Error {
  return new Error(
    'La comprobación de red superó el tiempo de espera. Espere una señal estable o inténtelo más tarde.',
  );
}

function offlineMessage(): Error {
  return new Error(
    'Sin conexión: el navegador indica que está fuera de línea. Conéctese a Internet e inténtelo de nuevo.',
  );
}

function networkFailureMessage(cause: unknown): Error {
  const hint = cause instanceof Error ? cause.message : String(cause);
  return new Error(
    `No se pudo comprobar la ruta hacia el servidor${hint ? ` (${hint})` : ''}. Revise Wi‑Fi o datos móviles, VPN, firewall y CORS del endpoint.`,
  );
}

/**
 * Throws if the runtime reports offline or the probe URL cannot be reached within
 * {@link SYNC_PREFLIGHT_TIMEOUT_MS}. Uses HEAD first, then a short GET whose body is cancelled
 * (for servers that reject HEAD).
 *
 * @param syncEndpointUrl Destination used when `dedicatedProbeUrl` is null/empty (same URL as POST/GET).
 * @param dedicatedProbeUrl Optional lightweight health URL (`SYNC_CONNECTIVITY_PROBE_*`); avoids probing huge GET bodies.
 */
export async function assertLikelyReachableForSync(
  syncEndpointUrl: string,
  dedicatedProbeUrl?: string | null,
): Promise<void> {
  if (typeof globalThis.navigator !== 'undefined' && globalThis.navigator.onLine === false) {
    throw offlineMessage();
  }

  const trimmed = dedicatedProbeUrl?.trim();
  const targetUrl = trimmed && trimmed.length > 0 ? trimmed : syncEndpointUrl;

  const signal =
    typeof globalThis.AbortSignal !== 'undefined' && typeof globalThis.AbortSignal.timeout === 'function'
      ? globalThis.AbortSignal.timeout(SYNC_PREFLIGHT_TIMEOUT_MS)
      : undefined;

  const runHead = (): Promise<Response> =>
    fetch(targetUrl, {
      method: 'HEAD',
      mode: 'cors',
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    });

  const runGetAndDiscardBody = async (): Promise<void> => {
    const res = await fetch(targetUrl, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    });
    await res.body?.cancel().catch(() => {});
  };

  try {
    await runHead();
  } catch (e) {
    if (isLikelyTimeoutAbort(e)) throw timeoutMessage();
    try {
      await runGetAndDiscardBody();
    } catch (e2) {
      if (isLikelyTimeoutAbort(e2)) throw timeoutMessage();
      throw networkFailureError(e2);
    }
  }
}

function networkFailureError(cause: unknown): Error {
  if (typeof globalThis.navigator !== 'undefined' && globalThis.navigator.onLine === false) {
    return offlineMessage();
  }
  return networkFailureMessage(cause);
}
